<script lang="ts"></script>

<script setup lang="ts">
/**
 * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [EnterImei]
 * Модальное окно / экран "Введите IMEI"
 */

// Hooks
import { computed, ref, watch } from "vue";

import HandedDevice from "@/components/handedDevice/handedDevice.vue";
// Components
import Modal from "@/components/modal/modal.vue";
import { BugTrackerSection, useBugTracker } from "@/helpers/bugTracker";
import it4CheckPhotoSetStatus from "@/hooks/requests/it4CheckPhotoSetStatus/it4CheckPhotoSetStatus";
import it4GetIMEIStatus from "@/hooks/requests/it4GetIMEIStatus/it4GetIMEIStatus";
import it4GetLoadPhotoFields from "@/hooks/requests/it4GetLoadPhotoFields/it4GetLoadPhotoFields";
import it4UpdateIMEI from "@/hooks/requests/it4UpdateIMEI/it4UpdateIMEI";
// import it4CheckContractSign from "@/hooks/requests/it4CheckContractSign/it4CheckContractSign";
import {
    useAnalyticsEventAddPhoto,
    useAnalyticsEventTiModel,
} from "@/hooks/useAnalytics/useAnalytics";
import useQRPath from "@/hooks/useQRPath/useQRPath";
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";
import useTradeInCalculation from "@/hooks/useTradeInCalculation/useTradeInCalculation";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import useValidate from "@/hooks/useValidate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useDownloadPhotosStore } from "@/stores/downloadPhotos/downloadPhotos";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
// Store
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import Field from "@/ui/field/field.vue";
// Icon
import CheckIcon from "@/ui/icons/check.vue";
import Input from "@/ui/input/input.vue";
import it4CheckImei from "~/tiw/hooks/requests/it4CheckImei/it4CheckImei";
// import { useSuccessfulEvaluationStore } from "@/stores/successfulEvaluation/successfulEvaluation";

const TIWStore = useTIWStore();
const ChooseDeviceStore = useChooseDeviceStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const DownloadPhotosStore = useDownloadPhotosStore();
// const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();

function onEditHandler() {
    HowYouRateDeviceStore.restoreToDefault();
    ChooseDeviceStore.restoreToDefault();
    TIWStore.switchStep("chooseDevice");
    useAnalyticsEventTiModel();
}

const EnterImeiValidation = computed(() => {
    if (!ChooseDeviceStore.field_imei.value.length) {
        return {
            status: true,
            message: "ok",
        };
    }
    const validate = useValidate({
        target: "imei",
        value: ChooseDeviceStore.field_imei.value,
    });
    return validate;
});

const howFindImeiBlock = ref<boolean>(false);
const imeiIsNotValid = ref<boolean>(false);
const imeiRetryEnter = ref<boolean>(false);
const deviceTerminated = ref<boolean>(false);
const errorMessage = ref<{ error: string; modelName?: string }>({
    error: "",
    modelName: "",
});
const imeiDataId = ref<string>("");
const previousEvaluationResultPrice = ref<string>("");
const updateInfoDeviceIdForReuse = ref<string>("");
const foundSignedContract = computed<boolean>((): boolean => {
    return updateInfoDeviceIdForReuse.value.length > 0;
});
const foundSignedContractText = computed<string>((): string => {
    const price: string =
        typeof HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale === "string"
            ? HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale
            : HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale.tradeInSale;

    const currency: string = HowYouRateDeviceStore.registeringDeviceData.currency;

    return useTranslate("tiw__enterImei__imei_foundSignedContract_text")
        .replace("[[PRICE]]", previousEvaluationResultPrice.value || price)
        .replace("[[CURRENCY]]", currency);
});

const showInfoAboutImei = () => (howFindImeiBlock.value = !howFindImeiBlock.value);

const loadingToNext = ref<boolean>(false);

watch(ChooseDeviceStore.field_imei, () => {
    deviceTerminated.value = false;
    updateInfoDeviceIdForReuse.value = "";

    if (ChooseDeviceStore.field_imei.value && EnterImeiValidation.value.status) {
        imeiIsNotValid.value = false;
    }
});

async function clickToClose() {
    TIWStore.closeWidget();
    useStoreResetter();
    TIWStore.INJECTED_TIW.current_api.onDrop();
}

const interval = ref();

// FIXME: это временный костыль. Нужно, чтобы api/tin/v1/site/quest/photos не падал с 404, если цена устройства ниже 20 долларов.
const wasGetLoadOhotoFieldsError = ref(false);

async function getImeiExecuter(id: string) {
    try {
        const imeiData = await it4GetIMEIStatus({
            token: TIWStore.WIDGET_DATA.token, // "d7bd115c-e845-4381-8f11-8044cbc11dfe",
            id, // "c6d7acf4-4657-4b79-b7c2-5e941fc3760d"
        });

        if (imeiData.status === "IN_PROGRESS") return;

        if (!imeiData.result || !imeiData.result?.imeiValidation) {
            useBugTracker(
                "EnterImei - it4GetIMEIStatus (response error)",
                BugTrackerSection.ERROR,
                {
                    response: imeiData,
                },
            );

            return;
        }

        // check updateInfo

        /*
    imeiData.result.imeiValidation.valid
    || imeiData.result.imeiValidation.unavailable
    */
        await useQRPath();

        try {
            // Для экрана "Загрузите фото сдаваемого устройства" - загрузить поля для загрузки фото
            const loadPhotoFieldsData = await it4GetLoadPhotoFields({
                deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                token: TIWStore.WIDGET_DATA.token,
                lang: TIWStore.standardizedLocale,
            });

            // useBugTracker("EnterImei - it4GetLoadPhotoFields (response)", BugTrackerSection.INFO, {
            //     response: loadPhotoFieldsData,
            // })

            DownloadPhotosStore.fieldsLineData = loadPhotoFieldsData;
        } catch (e) {
            // FIXME: это временный костыль. Нужно, чтобы api/tin/v1/site/quest/photos не падал с 404, если цена устройства ниже 20 долларов.
            wasGetLoadOhotoFieldsError.value = true;

            useBugTracker("EnterImei - it4GetLoadPhotoFields", BugTrackerSection.ERROR, {
                exception: e.message,
                request: {
                    deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                    token: TIWStore.WIDGET_DATA.token,
                    lang: TIWStore.standardizedLocale,
                },
            });
        }

        loadingToNext.value = false;

        clearInterval(interval.value);

        ChooseDeviceStore.field_imei.savedValue = ChooseDeviceStore.field_imei.value;

        // FIXME: wasGetLoadOhotoFieldsError - это временный костыль. Нужно, чтобы api/tin/v1/site/quest/photos не падал с 404, если цена устройства ниже 20 долларов.
        if (HowYouRateDeviceStore.skipPhotoGradingStep || wasGetLoadOhotoFieldsError.value) {
            TIWStore.switchStep("chooseHowToReceiveDiscount");
        } else {
            TIWStore.switchStep("downloadPhotos");
        }

        useAnalyticsEventAddPhoto();
    } catch (e) {
        useBugTracker("EnterImei - it4GetIMEIStatus", BugTrackerSection.ERROR, {
            exception: e.message,
            request: {
                token: TIWStore.WIDGET_DATA.token,
                id,
            },
        });
    }
}

function runIntervalImeiExecuter() {
    let intervalCount: number = 0;
    const MAX_INTERVAL_COUNT: number = 5;

    interval.value = setInterval(async () => {
        intervalCount++;

        await getImeiExecuter(imeiDataId.value);

        if (intervalCount >= MAX_INTERVAL_COUNT) {
            clearInterval(interval.value);
            interval.value = null;

            errorMessage.value = {
                error: useTranslate("tiw__enterImei__imei_uncorrect"),
            };

            imeiRetryEnter.value = true;
            loadingToNext.value = false;
        }
    }, 5000);
}

async function onClickReEvaluate() {
    updateInfoDeviceIdForReuse.value = "";
    loadingToNext.value = true;
    imeiRetryEnter.value = false;
    runIntervalImeiExecuter();
}

async function onClickApplyEvaluate() {
    HowYouRateDeviceStore.registeringDeviceData.deviceId = updateInfoDeviceIdForReuse.value;

    TIWStore.switchStep("chooseHowToReceiveDiscount");
}

async function clickToNext() {
    loadingToNext.value = true;
    imeiRetryEnter.value = false;

    try {
        const checkImei = await it4CheckImei({
            session: TIWStore.SESSION_TOKEN,
            imei: ChooseDeviceStore.field_imei.value,
        });

        const checkedImei = checkImei[0];

        if (["ERROR", "BLOCK", "BLOCKED"].includes(checkedImei.status)) {
            // errorMessage.value = {
            //   error: useTranslate('tiw__enterImei__imei_notAccepted'),
            // }
            // imeiRetryEnter.value = true
            // loadingToNext.value = false
            // return
        }

        const imeiData = await it4UpdateIMEI({
            // article: TIWStore.INJECTED_GOOD.article,
            token: TIWStore.WIDGET_DATA.token,
            imei: ChooseDeviceStore.field_imei.value,
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        });

        imeiDataId.value = imeiData.id;

        const updateInfo_Status: string = imeiData.result.updateInfo?.status || "";
        const updateInfo_DeviceId: string = imeiData.result.updateInfo?.deviceIdForReuse || "";
        const updateInfo_PhotosetId: string = imeiData.result.updateInfo?.photosetId || "";

        if (updateInfo_DeviceId && updateInfo_PhotosetId) {
            if (updateInfo_Status === "found_order") {
                // заказ уже есть - устройство не принимаем
                deviceTerminated.value = true;
                loadingToNext.value = false;
                return;
            }

            if (updateInfo_Status === "found_but_too_late") {
                runIntervalImeiExecuter();
                return;
            }

            try {
                // есть предыдущая оценка с подписанным договором
                // значит сохраняем предыдущий deviceId из deviceIdForReuse
                // с предварительным запросом статуса фотосета (там есть результат оценки)
                const photoSetStatus = await it4CheckPhotoSetStatus({
                    deviceId: updateInfo_DeviceId,
                    photosetId: updateInfo_PhotosetId,
                    token: TIWStore.WIDGET_DATA.token,
                    lang: TIWStore.standardizedLocale,
                });

                previousEvaluationResultPrice.value = String(photoSetStatus.price);

                useTradeInCalculation({
                    tradeInPrice: previousEvaluationResultPrice.value,
                });
            } catch (e) {
                useBugTracker("EnterImei - it4CheckPhotoSetStatus", BugTrackerSection.ERROR, {
                    exception: e.message,
                    request: {
                        deviceId: updateInfo_DeviceId,
                        photosetId: updateInfo_PhotosetId,
                        token: TIWStore.WIDGET_DATA.token,
                        lang: TIWStore.standardizedLocale,
                    },
                });
            }

            loadingToNext.value = false;
            updateInfoDeviceIdForReuse.value = updateInfo_DeviceId;
        } else {
            runIntervalImeiExecuter();
        }
    } catch (e) {
        useBugTracker("EnterImei - it4UpdateIMEI", BugTrackerSection.ERROR, {
            exception: e.message,
            request: {
                // article: TIWStore.INJECTED_GOOD.article,
                token: TIWStore.WIDGET_DATA.token,
                imei: ChooseDeviceStore.field_imei.value,
                deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            },
        });

        loadingToNext.value = false;
    }
}
const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});
</script>

<template>
    <Modal class="tiw__step__enterImei">
        <template #header>
            <p class="tiw__h2">
                {{ useTranslate("tiw__enterImei__title") }}
            </p>
        </template>

        <div>
            <HandedDevice
                :device-price="handedDevicePrice"
                :device-chain="ChooseDeviceStore.deviceChain"
                :device-state="HowYouRateDeviceStore.rate"
                :device-preview="ChooseDeviceStore.deviceImagePreview"
                :device-grade="null"
                :device-no-grade="false"
                :device-terminated="false"
                :on-edit="onEditHandler"
            />
            <form name="enterImeiForm">
                <!-- Поле "IMEI" -->
                <Field :is-error="!EnterImeiValidation.status">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__enterImei__imei") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="ChooseDeviceStore.field_imei.value"
                            type="text"
                            :placeholder="useTranslate('tiw__enterImei__imei')"
                            name="imei"
                            maxlength="15"
                            inputmode="numeric"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!EnterImeiValidation.status" #hintBottom>
                        <span>
                            {{ EnterImeiValidation.message }}
                        </span>
                    </template>
                </Field>

                <template v-if="loadingToNext">
                    <div class="tiw__step__enterImei__status">
                        <CheckIcon variant="circled" />
                        <span>
                            {{ useTranslate("tiw__enterImei__status") }}
                        </span>
                    </div>
                </template>

                <template v-if="imeiRetryEnter">
                    <div class="tiw__step__enterImei__warn">
                        <div
                            v-if="errorMessage.modelName"
                            class="tiw__step__enterImei__warn__model"
                        >
                            <div class="tiw__step__enterImei__warn__model__title">
                                {{ useTranslate("tiw__enterImei__yourModel") }}
                            </div>
                            <span class="tiw__step__enterImei__warn__model__phone">
                                {{ errorMessage.modelName }}
                            </span>
                        </div>
                        {{ errorMessage.error }}
                    </div>
                    <div class="tiw_group tiw_group-sb" :class="{ tiw_mb16: foundSignedContract }">
                        <Button
                            color="link"
                            variant="link"
                            type="button"
                            class="tiw_fs-12"
                            @click="showInfoAboutImei"
                        >
                            <span>
                                {{ useTranslate("tiw__enterImei__howFindImei") }}
                            </span>
                        </Button>
                        <Button
                            type="button"
                            class="tiw__step__enterImei__btn-close"
                            @click="clickToNext"
                        >
                            <span>{{ useTranslate("tiw__continue") }}</span>
                        </Button>
                    </div>
                </template>
                <template v-else-if="imeiIsNotValid">
                    <div class="tiw__step__enterImei__warn">
                        <div
                            v-if="errorMessage.modelName"
                            class="tiw__step__enterImei__warn__model"
                        >
                            <div class="tiw__step__enterImei__warn__model__title">
                                {{ useTranslate("tiw__enterImei__yourModel") }}
                            </div>
                            <span class="tiw__step__enterImei__warn__model__phone">
                                {{ errorMessage.modelName }}
                            </span>
                        </div>
                        {{ errorMessage.error }}
                    </div>
                    <div class="tiw_group tiw_group-sb" :class="{ tiw_mb16: foundSignedContract }">
                        <Button
                            color="link"
                            variant="link"
                            type="button"
                            class="tiw_fs-12"
                            @click="showInfoAboutImei"
                        >
                            <span>
                                {{ useTranslate("tiw__enterImei__howFindImei") }}
                            </span>
                        </Button>
                        <Button
                            v-if="errorMessage.modelName || imeiRetryEnter"
                            type="button"
                            class="tiw__step__enterImei__btn-close"
                            @click="onEditHandler"
                        >
                            <span>{{ useTranslate("tiw__continue") }}</span>
                        </Button>
                        <Button
                            v-else
                            type="button"
                            class="tiw__step__enterImei__btn-close"
                            @click="clickToClose"
                        >
                            <span>{{ useTranslate("tiw__close") }}</span>
                        </Button>
                    </div>
                </template>
                <template v-else-if="deviceTerminated">
                    <div class="tiw__step__enterImei__warn">
                        {{ useTranslate("tiw__step__enterImei__warn__deviceTerminated") }}
                    </div>
                    <Button
                        type="button"
                        class="tiw__step__enterImei__btn-close"
                        @click="clickToNext"
                    >
                        <span>{{ useTranslate("tiw__continue") }}</span>
                    </Button>
                </template>
                <template v-else>
                    <div class="tiw_group tiw_group-sb" :class="{ tiw_mb16: foundSignedContract }">
                        <Button
                            color="link"
                            variant="link"
                            type="button"
                            class="tiw_fs-12"
                            @click="showInfoAboutImei"
                        >
                            <span>
                                {{ useTranslate("tiw__enterImei__howFindImei") }}
                            </span>
                        </Button>
                        <Button
                            type="button"
                            class="tiw__step__enterImei__btn-next"
                            :disabled="
                                !ChooseDeviceStore.field_imei.value.length ||
                                !EnterImeiValidation.status ||
                                loadingToNext ||
                                foundSignedContract
                            "
                            :loading="loadingToNext"
                            @click="clickToNext"
                        >
                            <span>
                                {{ useTranslate("tiw__continue") }}
                            </span>
                        </Button>
                    </div>
                </template>

                <template v-if="foundSignedContract">
                    <div class="tiw__step__enterImei__foundSignedContract tiw_mb8">
                        <div class="tiw__step__enterImei__foundSignedContract__title">
                            {{ useTranslate("tiw__enterImei__imei_foundSignedContract_title") }}
                        </div>
                        <div
                            class="tiw__step__enterImei__foundSignedContract__text"
                            v-html="foundSignedContractText"
                        />
                    </div>
                    <div class="tiw__step__enterImei__btn">
                        <Button
                            type="button"
                            color="light"
                            class="tiw__step__enterImei__btn-re-evaluate"
                            @click="onClickReEvaluate"
                        >
                            <span>{{
                                useTranslate("tiw__enterImei__imei_foundSignedContract_re_evaluate")
                            }}</span>
                        </Button>

                        <Button
                            type="button"
                            class="tiw__step__enterImei__btn-apply-evaluate"
                            @click="onClickApplyEvaluate"
                        >
                            <span>{{
                                useTranslate(
                                    "tiw__enterImei__imei_foundSignedContract_apply_evaluate",
                                )
                            }}</span>
                        </Button>
                    </div>
                </template>

                <!-- Инфо-блок с пояснением как узнать свой IMEI -->
                <template v-if="howFindImeiBlock">
                    <div class="tiw__step__enterImei__info">
                        <p class="tiw__title">
                            {{ useTranslate("tiw__enterImei__howFindImei") }}
                        </p>
                        <ul class="tiw__ul tiw__ul_disc tiw_fs-14">
                            <li v-html="useTranslate('tiw__enterImei__howFindImei_step1')" />
                            <li>
                                {{ useTranslate("tiw__enterImei__howFindImei_step2") }}
                            </li>
                        </ul>
                        <figure class="tiw_tc">
                            <img
                                src="https://mw2.breezyx.space/img/iPhone-dial-imei.png"
                                alt="iPhone dial *#06#"
                            />
                        </figure>
                    </div>
                </template>
            </form>
        </div>
    </Modal>
</template>

<style lang="scss" src="./enterImei.scss"></style>
